// Music theory helpers for vocal exercises: scale construction,
// key-aware note spelling, and MusicXML generation for OSMD rendering.

export const SCALE_TYPES = {
  major: { intervals: [0, 2, 4, 5, 7, 9, 11], mode: 'major', kind: 'diatonic' },
  naturalMinor: { intervals: [0, 2, 3, 5, 7, 8, 10], mode: 'minor', kind: 'diatonic' },
  harmonicMinor: { intervals: [0, 2, 3, 5, 7, 8, 11], mode: 'minor', kind: 'diatonic' },
  // Arabian (double harmonic major): root, b2, 3, 4, 5, b6, 7
  arabian: { intervals: [0, 1, 4, 5, 7, 8, 11], mode: 'major', kind: 'diatonic' },
  // Symmetric scales: no diatonic letter sequence or key signature; spelled
  // from per-pitch-class tables and notated with explicit accidentals
  wholeTone: { intervals: [0, 2, 4, 6, 8, 10], mode: 'major', kind: 'symmetric' },
  chromatic: { intervals: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], mode: 'major', kind: 'symmetric' }
};

// The 12 keys, spelled per mode (e.g. pitch class 1 is Db major but C# minor)
export const KEYS = [
  { pc: 0, major: 'C', minor: 'C' },
  { pc: 1, major: 'Db', minor: 'C#' },
  { pc: 2, major: 'D', minor: 'D' },
  { pc: 3, major: 'Eb', minor: 'Eb' },
  { pc: 4, major: 'E', minor: 'E' },
  { pc: 5, major: 'F', minor: 'F' },
  { pc: 6, major: 'F#', minor: 'F#' },
  { pc: 7, major: 'G', minor: 'G' },
  { pc: 8, major: 'Ab', minor: 'G#' },
  { pc: 9, major: 'A', minor: 'A' },
  { pc: 10, major: 'Bb', minor: 'Bb' },
  { pc: 11, major: 'B', minor: 'B' }
];

const MAJOR_FIFTHS = { C: 0, G: 1, D: 2, A: 3, E: 4, B: 5, 'F#': 6, F: -1, Bb: -2, Eb: -3, Ab: -4, Db: -5 };
const MINOR_FIFTHS = { A: 0, E: 1, B: 2, 'F#': 3, 'C#': 4, 'G#': 5, D: -1, G: -2, C: -3, F: -4, Bb: -5, Eb: -6 };

const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const NATURAL_SEMITONE = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export function getTonicName(keyEntry, scaleType) {
  return SCALE_TYPES[scaleType].mode === 'major' ? keyEntry.major : keyEntry.minor;
}

export function prettyNoteName(name) {
  return name.replace('b', '♭').replace('#', '♯');
}

export function getKeyFifths(tonicName, scaleType) {
  const mode = SCALE_TYPES[scaleType].mode;
  const table = mode === 'major' ? MAJOR_FIFTHS : MINOR_FIFTHS;
  return table[tonicName] ?? 0;
}

// Note name for the audio engine (soundfont expects sharp-based names like "C#4")
export function midiToPlayableNote(midi) {
  return SHARP_NAMES[midi % 12] + (Math.floor(midi / 12) - 1);
}

// Number of diatonic scale steps to the top note for each range option
const RANGE_STEPS = { '1': 7, '1.5': 11, '2': 14 };
// Total semitone span for each range option (used by the symmetric scales)
const RANGE_SEMITONES = { '1': 12, '1.5': 19, '2': 24 };

// Per-pitch-class spellings for the symmetric scales, which have no diatonic
// letter sequence to follow. Convention: sharps ascending, flats descending
// (chromatic), and flat spellings throughout for flat-named keys.
const SHARP_SPELLINGS = [
  { step: 'C', alter: 0 }, { step: 'C', alter: 1 }, { step: 'D', alter: 0 }, { step: 'D', alter: 1 },
  { step: 'E', alter: 0 }, { step: 'F', alter: 0 }, { step: 'F', alter: 1 }, { step: 'G', alter: 0 },
  { step: 'G', alter: 1 }, { step: 'A', alter: 0 }, { step: 'A', alter: 1 }, { step: 'B', alter: 0 }
];
const FLAT_SPELLINGS = [
  { step: 'C', alter: 0 }, { step: 'D', alter: -1 }, { step: 'D', alter: 0 }, { step: 'E', alter: -1 },
  { step: 'E', alter: 0 }, { step: 'F', alter: 0 }, { step: 'G', alter: -1 }, { step: 'G', alter: 0 },
  { step: 'A', alter: -1 }, { step: 'A', alter: 0 }, { step: 'B', alter: -1 }, { step: 'B', alter: 0 }
];

// Spell a midi note from a pitch-class table, keeping the tonic's own spelling
// (e.g. an F# tonic stays F# even in the flat table, where pc 6 is Gb).
// None of these spellings cross the B/C boundary, so the octave is direct.
function spellMidi(midi, table, tonicPc, tonicSpelling) {
  const pc = ((midi % 12) + 12) % 12;
  const { step, alter } = pc === tonicPc ? tonicSpelling : table[pc];
  return { step, alter, octave: Math.floor(midi / 12) - 1, midi };
}

/**
 * Builds the note sequence for a scale exercise.
 * Each note: { step, alter, octave, midi }. Diatonic scales walk the letter
 * sequence (so alterations follow from the intervals); symmetric scales are
 * spelled from the pitch-class tables above.
 */
export function buildScale({ scaleType, keyEntry, startOctave, range, direction }) {
  const { intervals, kind } = SCALE_TYPES[scaleType];
  const tonicName = getTonicName(keyEntry, scaleType);
  const tonicLetter = tonicName[0];
  const tonicAlter = tonicName.includes('#') ? 1 : tonicName.includes('b') ? -1 : 0;
  const tonicLetterIdx = LETTERS.indexOf(tonicLetter);
  const tonicMidi = (startOctave + 1) * 12 + NATURAL_SEMITONE[tonicLetter] + tonicAlter;

  let ascending;
  let descending;
  if (kind === 'symmetric') {
    const span = RANGE_SEMITONES[range] ?? 12;
    const tonicPc = ((tonicMidi % 12) + 12) % 12;
    const tonicSpelling = { step: tonicLetter, alter: tonicAlter };
    const isFlatKey = tonicName.includes('b');
    const step = scaleType === 'chromatic' ? 1 : 2;

    const ascMidis = [];
    for (let offset = 0; offset <= span; offset += step) {
      ascMidis.push(tonicMidi + offset);
    }
    const upTable = isFlatKey ? FLAT_SPELLINGS : SHARP_SPELLINGS;
    // Chromatic descends with flats regardless of key; whole tone keeps one spelling
    const downTable = scaleType === 'chromatic' ? FLAT_SPELLINGS : upTable;
    ascending = ascMidis.map((m) => spellMidi(m, upTable, tonicPc, tonicSpelling));
    descending = [...ascMidis].reverse().map((m) => spellMidi(m, downTable, tonicPc, tonicSpelling));
  } else {
    const stepsUp = RANGE_STEPS[range] ?? 7;
    ascending = [];
    for (let i = 0; i <= stepsUp; i++) {
      const targetMidi = tonicMidi + intervals[i % 7] + 12 * Math.floor(i / 7);
      const letterIdx = (tonicLetterIdx + i) % 7;
      const letterOctave = startOctave + Math.floor((tonicLetterIdx + i) / 7);
      const step = LETTERS[letterIdx];
      const naturalMidi = (letterOctave + 1) * 12 + NATURAL_SEMITONE[step];
      ascending.push({ step, alter: targetMidi - naturalMidi, octave: letterOctave, midi: targetMidi });
    }
    descending = [...ascending].reverse();
  }

  let notes;
  if (direction === 'up') {
    notes = ascending;
  } else if (direction === 'down') {
    notes = descending;
  } else {
    notes = [...ascending, ...descending.slice(1)];
  }

  const fifths = kind === 'symmetric' ? 0 : getKeyFifths(tonicName, scaleType);
  return { notes, fifths, clef: chooseClef(notes) };
}

// Diatonic staff position (one unit per letter step): C4 = 28, E4 = 30, ...
function diatonicPosition(note) {
  return note.octave * 7 + LETTERS.indexOf(note.step);
}

// Clef candidates with their staff spans (bottom/top line as diatonic positions).
// 'treble8vb' is the choral tenor clef (G clef sounding an octave lower); notes are
// written an octave up (+7 letter steps), so it fits low-plus-high vocal ranges.
const CLEFS = {
  treble: { bottom: 30, top: 38, writtenShift: 0, penalty: 0 },
  bass: { bottom: 18, top: 26, writtenShift: 0, penalty: 0 },
  treble8vb: { bottom: 30, top: 38, writtenShift: 7, penalty: 2 }
};

function ledgerLines(pos, bottom, top) {
  if (pos < bottom) return Math.ceil((bottom - pos) / 2);
  if (pos > top) return Math.ceil((pos - top) / 2);
  return 0;
}

// Picks the clef that minimizes total ledger lines across the exercise.
// The 8vb clef carries a small penalty so plain clefs win unless it clearly helps.
function chooseClef(notes) {
  let best = 'treble';
  let bestCost = Infinity;
  for (const [name, clef] of Object.entries(CLEFS)) {
    const cost =
      clef.penalty +
      notes.reduce((sum, n) => sum + ledgerLines(diatonicPosition(n) + clef.writtenShift, clef.bottom, clef.top), 0);
    if (cost < bestCost) {
      best = name;
      bestCost = cost;
    }
  }
  return best;
}

// --- Guided exercise (uploaded snippet) helpers ---

// Transposes parsed exercise measures (from exerciseParser) by `semitones`
// and respells every note for the target major key. Rests pass through.
export function transposeExercise(measures, semitones, targetTonicPc) {
  const tonicName = KEYS[targetTonicPc].major;
  const table = tonicName.includes('b') ? FLAT_SPELLINGS : SHARP_SPELLINGS;
  const tonicLetter = tonicName[0];
  const tonicAlter = tonicName.includes('#') ? 1 : tonicName.includes('b') ? -1 : 0;
  const tonicSpelling = { step: tonicLetter, alter: tonicAlter };

  const spelled = measures.map((measure) =>
    measure.map((note) => {
      if (note.rest) return { ...note };
      const midi = note.midi + semitones;
      return {
        ...spellMidi(midi, table, targetTonicPc, tonicSpelling),
        rest: false,
        quarters: note.quarters,
        lyric: note.lyric || null
      };
    })
  );
  return { measures: spelled, fifths: MAJOR_FIFTHS[tonicName] ?? 0, tonicName };
}

export function chooseClefForNotes(notes) {
  return chooseClef(notes.filter((n) => !n.rest));
}

// quarters -> MusicXML duration (divisions = 4), note type and dot
const DURATION_TYPES = [
  { quarters: 4, type: 'whole', dot: false },
  { quarters: 3, type: 'half', dot: true },
  { quarters: 2, type: 'half', dot: false },
  { quarters: 1.5, type: 'quarter', dot: true },
  { quarters: 1, type: 'quarter', dot: false },
  { quarters: 0.75, type: 'eighth', dot: true },
  { quarters: 0.5, type: 'eighth', dot: false },
  { quarters: 0.25, type: '16th', dot: false }
];

function durationInfo(quarters) {
  let best = DURATION_TYPES[0];
  for (const d of DURATION_TYPES) {
    if (Math.abs(d.quarters - quarters) < Math.abs(best.quarters - quarters)) best = d;
  }
  return best;
}

function escapeXml(text) {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Generates MusicXML for a transposed exercise snippet, preserving the
 * source rhythm and measure boundaries.
 */
export function generateExerciseMusicXML(measures, fifths, beats, beatType, clef) {
  const clefSign = clef === 'bass' ? 'F' : 'G';
  const clefLine = clef === 'bass' ? '4' : '2';
  const clefOctaveChange = clef === 'treble8vb' ? '\n          <clef-octave-change>-1</clef-octave-change>' : '';

  const measuresXML = measures
    .map((measureNotes, measureIdx) => {
      const measureState = {};
      const notesXML = measureNotes
        .map((note) => {
          const info = durationInfo(note.quarters);
          const duration = Math.max(1, Math.round(note.quarters * 4));
          const dotTag = info.dot ? '\n        <dot/>' : '';
          if (note.rest) {
            return `      <note>
        <rest/>
        <duration>${duration}</duration>
        <type>${info.type}</type>${dotTag}
      </note>`;
          }
          const stateKey = note.step + note.octave;
          const currentAlter = stateKey in measureState ? measureState[stateKey] : keySignatureAlter(note.step, fifths);
          const needsAccidental = note.alter !== currentAlter;
          measureState[stateKey] = note.alter;
          return `      <note>
        <pitch>
          <step>${note.step}</step>${note.alter !== 0 ? `\n          <alter>${note.alter}</alter>` : ''}
          <octave>${note.octave}</octave>
        </pitch>
        <duration>${duration}</duration>
        <type>${info.type}</type>${dotTag}${needsAccidental ? `\n        <accidental>${ACCIDENTAL_NAMES[note.alter]}</accidental>` : ''}${note.lyric ? `\n        <lyric><syllabic>single</syllabic><text>${escapeXml(note.lyric)}</text></lyric>` : ''}
      </note>`;
        })
        .join('\n');

      const attributes =
        measureIdx === 0
          ? `      <attributes>
        <divisions>4</divisions>
        <key>
          <fifths>${fifths}</fifths>
          <mode>major</mode>
        </key>
        <time>
          <beats>${beats}</beats>
          <beat-type>${beatType}</beat-type>
        </time>
        <clef>
          <sign>${clefSign}</sign>
          <line>${clefLine}</line>${clefOctaveChange}
        </clef>
      </attributes>\n`
          : '';

      return `    <measure number="${measureIdx + 1}">\n${attributes}${notesXML}\n    </measure>`;
    })
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 3.1 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="3.1">
  <part-list>
    <score-part id="P1">
      <part-name>Exercise</part-name>
    </score-part>
  </part-list>
  <part id="P1">
${measuresXML}
  </part>
</score-partwise>`;
}

function keySignatureAlter(step, fifths) {
  const sharps = ['F', 'C', 'G', 'D', 'A', 'E', 'B'];
  const flats = ['B', 'E', 'A', 'D', 'G', 'C', 'F'];
  if (fifths > 0 && sharps.slice(0, fifths).includes(step)) return 1;
  if (fifths < 0 && flats.slice(0, -fifths).includes(step)) return -1;
  return 0;
}

const ACCIDENTAL_NAMES = { '-2': 'flat-flat', '-1': 'flat', 0: 'natural', 1: 'sharp', 2: 'double-sharp' };

/**
 * Generates a MusicXML document for a scale: quarter notes in 4/4,
 * with key signature and explicit accidentals where a note departs from it.
 */
export function generateScaleMusicXML(notes, fifths, mode, clef) {
  const clefSign = clef === 'bass' ? 'F' : 'G';
  const clefLine = clef === 'bass' ? '4' : '2';
  const clefOctaveChange = clef === 'treble8vb' ? '\n          <clef-octave-change>-1</clef-octave-change>' : '';

  const measures = [];
  for (let i = 0; i < notes.length; i += 4) {
    measures.push(notes.slice(i, i + 4));
  }

  const measuresXML = measures
    .map((measureNotes, measureIdx) => {
      // Accidentals apply for the rest of the measure to the same letter+octave
      const measureState = {};
      const notesXML = measureNotes
        .map((note) => {
          const stateKey = note.step + note.octave;
          const currentAlter = stateKey in measureState ? measureState[stateKey] : keySignatureAlter(note.step, fifths);
          const needsAccidental = note.alter !== currentAlter;
          measureState[stateKey] = note.alter;
          return `      <note>
        <pitch>
          <step>${note.step}</step>${note.alter !== 0 ? `\n          <alter>${note.alter}</alter>` : ''}
          <octave>${note.octave}</octave>
        </pitch>
        <duration>1</duration>
        <type>quarter</type>${needsAccidental ? `\n        <accidental>${ACCIDENTAL_NAMES[note.alter]}</accidental>` : ''}
      </note>`;
        })
        .join('\n');

      const attributes =
        measureIdx === 0
          ? `      <attributes>
        <divisions>1</divisions>
        <key>
          <fifths>${fifths}</fifths>
          <mode>${mode}</mode>
        </key>
        <time>
          <beats>4</beats>
          <beat-type>4</beat-type>
        </time>
        <clef>
          <sign>${clefSign}</sign>
          <line>${clefLine}</line>${clefOctaveChange}
        </clef>
      </attributes>\n`
          : '';

      return `    <measure number="${measureIdx + 1}">\n${attributes}${notesXML}\n    </measure>`;
    })
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 3.1 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="3.1">
  <part-list>
    <score-part id="P1">
      <part-name>Vocal Exercise</part-name>
    </score-part>
  </part-list>
  <part id="P1">
${measuresXML}
  </part>
</score-partwise>`;
}
